import { ChangeDetectionStrategy,Component, inject, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormField, MatHint, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { languages } from '@codemirror/language-data';

import { PageSimpleContentComponent, PageStatus } from 'app/layout/content';
import { ErrorComponent } from 'app/shared/components/error.component';
import { SaveButtonComponent } from 'app/shared/components/save-button.component';
import { SecretInputToggleComponent } from 'app/shared/components/secret-input-toggle.component';
import { BuiltInExternalPhotoSourceConfig, ExternalPhotoSourceStatus } from 'app/shared/graph';

import { ExternalPhotosConfigService } from './external-photos-config.service';

@Component({
    templateUrl: './built-in-external-photo-source-edit-page.component.html',
    styleUrls: ['./built-in-external-photo-source-edit-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageSimpleContentComponent, FormsModule, MatFormField, MatLabel, MatInput, SecretInputToggleComponent, MatSuffix, MatHint, SaveButtonComponent, ErrorComponent]
})
export class BuiltInExternalPhotoSourceEditPageComponent implements OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly photosConfigService = inject(ExternalPhotosConfigService);
    private readonly router = inject(Router);

    public mode: 'edit' | 'create';
    public title = '';
    public storeId: string;
    public photoSourceId: string;
    public pageStatus: PageStatus = PageStatus.loading;
    public pageError?: Error;
    public languages = languages;

    public builtInExternalSource?: BuiltInExternalPhotoSourceConfig;
    public appSecret ?: string;
    private redirectTimerId?: number;

    constructor() {
        this.storeId = this.route.snapshot.params.storeId;
        this.photoSourceId = this.route.snapshot.params.photoSourceId;
        this.mode = this.route.snapshot.data.mode;
        if (this.mode === 'edit') {
            this.title = 'Edit BuiltIn External Photo Source';
            this.photosConfigService.loadBuiltInExternalPhotoSource(this.storeId, this.photoSourceId)
                .subscribe({
                    next: source => {
                        if (!source) {
                            this.pageStatus = PageStatus.loadError;
                            this.pageError = new Error('Photo source not found');
                            return;
                        }

                        this.builtInExternalSource = source;
                        this.pageStatus = PageStatus.loaded;
                    },
                    error: err => {
                        this.pageStatus = PageStatus.loadError;
                        this.pageError = err;
                    }
                });
        } else {
            this.title = 'Add New BuiltIn External Photo Source';
            this.photosConfigService.loadBuiltInExternalPhotoSourceDefinitions()
                .subscribe({
                    next: sources => {
                        const source = sources.find(s => s.providerId == this.photoSourceId);
                        if (!source) {
                            this.pageStatus = PageStatus.loadError;
                            this.pageError = new Error('Photo source not found');
                            return;
                        }

                        this.builtInExternalSource = {
                            icon: source.iconUrl,
                            label: source.label,
                            photoSourceId: source.providerId,
                            appId: '',
                            status: ExternalPhotoSourceStatus.Disabled,
                            store: { id: this.storeId } as any
                        };
                        this.pageStatus = PageStatus.loaded;
                    },
                    error: err => {
                        this.pageStatus = PageStatus.loadError;
                        this.pageError = err;
                    }
                });
            this.pageStatus = PageStatus.loaded;
        }

    }

    save() {
        if (!this.builtInExternalSource) {
            return;
        }
        if (this.mode == 'edit') {
            this.photosConfigService.updateBuiltInExternalPhotoSource({
                storeId: this.storeId,
                photoSourceId: this.photoSourceId,
                appId: this.builtInExternalSource.appId,
                appSecret: this.appSecret
            }).subscribe({
                next: this.completeSave.bind(this),
                error: this.saveError.bind(this)
            });
        } else {
            if (!this.appSecret) {
                return;
            }

            let input = {
                storeId: this.storeId,
                photoSourceId: this.photoSourceId,
                appId: this.builtInExternalSource.appId,
                appSecret: this.appSecret
            };
            this.photosConfigService.createBuiltInExternalPhotoSource(input).subscribe({
                next: this.completeSave.bind(this),
                error: this.saveError.bind(this)
            });
        }

        this.pageStatus = PageStatus.saving;
    }

    private saveError(err: Error) {
        this.pageStatus = PageStatus.loadError;
        this.pageError = err;
    }

    private completeSave(source: BuiltInExternalPhotoSourceConfig) {
        this.pageStatus = PageStatus.loaded;
        this.builtInExternalSource = source;
        this.appSecret = undefined;
        this.redirectTimerId = window.setTimeout(() => {
            this.router.navigate(['stores', this.storeId, 'settings', 'external-photo-sources']);
        }, 500);
    }

    ngOnDestroy(): void {
        if (this.redirectTimerId) {
            clearTimeout(this.redirectTimerId);
            this.redirectTimerId = undefined;
        }
    }
}
